import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Logo } from './Logo'
import { Search } from '../search/Search'
import { useIsMobile } from '../../lib/hooks/useIsMobile'
import { useRouter } from 'next/router'
import { Contact } from './Contact'
import LanguageSwitcher from './LanguageSwitcher'
import { usePageType } from '../../lib/hooks/usePageType'
import { useLocationContext } from '../context/LocationContext'
import { HEADER_CATEGORIES } from '../../lib/constants'
import { getHref } from '../../lib/helper/helper'
import { useHeaderCategories } from './hooks/useHeaderCategories'
import { useCategoryFit } from './hooks/useCategoryFit'
import { CityCategoriesDesktop } from './components/CityCategoriesDesktop'
import { CityCategoriesMobileList } from './components/CityCategoriesMobileList'
import { CategoryDropdown } from './components/CategoryDropdown'

export function Header() {
  const { t, i18n } = useTranslation('common')
  const isMobile = useIsMobile(768)
  const [searchFocused, setSearchFocused] = useState<boolean>(false)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [languageMenuOpen, setLanguageMenuOpen] = useState<boolean>(false)
  const [languageCloseSignal, setLanguageCloseSignal] = useState<number>(0)
  const moreButtonRef = useRef<HTMLButtonElement | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{
    left: number
    top: number
  } | null>(null)
  const centerRef = useRef<HTMLDivElement | null>(null)
  const router = useRouter()
  const { pathname, query } = router
  const isNotRoot = pathname !== '/'
  const { type } = usePageType()
  const isCityPage = type === 'city'
  const isCategoryPage = type === 'category'
  const showLocationCategories = isCityPage || isCategoryPage
  const { locationId } = useLocationContext()
  // categories are now derived via hook

  const showSearchAsIcon = isMobile && isNotRoot && !showLocationCategories

  useEffect(() => {
    setSearchFocused(false)
    setMenuOpen(false)
    setDropdownOpen(false)
    setDropdownCoords(null)
  }, [pathname])

  // Ensure only one menu open at a time when language opens
  useEffect(() => {
    if (languageMenuOpen) {
      setMenuOpen(false)
      setDropdownOpen(false)
      setDropdownCoords(null)
    }
  }, [languageMenuOpen])

  // Lock body scroll when mobile categories menu is open
  useEffect(() => {
    const className = 'mobile-menu-open'
    if (isMobile && menuOpen) {
      document.body.classList.add(className)
    } else {
      document.body.classList.remove(className)
    }
    return () => document.body.classList.remove(className)
  }, [isMobile, menuOpen])

  // Build header categories for current i18n.language
  const headerCategoryLabels = useMemo(() => {
    return HEADER_CATEGORIES.map((item) => ({
      id: item._id,
      label: (item as any)[i18n.language] as string,
    }))
  }, [i18n.language])

  const matchedHeaderCategories = useHeaderCategories({
    isCityPage,
    locationId,
    language: i18n.language,
    headerLabels: headerCategoryLabels,
    queryCity: query.city,
  })

  const numVisibleCategories = useCategoryFit(
    showLocationCategories,
    matchedHeaderCategories,
    centerRef.current,
  )

  const renderSearchAsIcon = () => {
    return (
      <button
        onClick={() => {
          setSearchFocused(true)
        }}
        className="flex items-center"
        aria-label="Search SIGHT2SIGHT"
      >
        <span className="text-xl far fa-magnifying-glass" />
      </button>
    )
  }

  const renderCityCategoriesDesktop = () => (
    <CityCategoriesDesktop
      categories={matchedHeaderCategories}
      language={i18n.language}
      numVisible={numVisibleCategories}
      moreButtonRef={moreButtonRef}
      onToggleDropdown={(open) => {
        // When category dropdown opens, close others
        if (open) {
          setMenuOpen(false)
          setLanguageCloseSignal((v) => v + 1)
        } else {
          setDropdownCoords(null)
        }
        setDropdownOpen(open)
      }}
      isDropdownOpen={dropdownOpen}
      onComputeDropdownCoords={setDropdownCoords}
      t={t}
      getHref={getHref}
    />
  )

  const renderCityCategoriesMobileMenu = () => (
    <CityCategoriesMobileList
      categories={matchedHeaderCategories}
      language={i18n.language}
      onItemClick={() => setMenuOpen(false)}
      getHref={getHref}
    />
  )

  // Close on outside click and on Escape (no refs; event delegation)
  useEffect(() => {
    if (!dropdownOpen) return
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        target.closest('[data-header-more-button]') ||
        target.closest('[data-header-dropdown]')
      ) {
        return
      }
      setDropdownOpen(false)
      setDropdownCoords(null)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDropdownOpen(false)
        setDropdownCoords(null)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [dropdownOpen])

  // Reposition dropdown on resize (let it naturally scroll with the page)
  useEffect(() => {
    if (!dropdownOpen) return
    const handle = () => {
      const btn = moreButtonRef.current
      if (!btn) {
        setDropdownOpen(false)
        setDropdownCoords(null)
        return
      }
      const rect = btn.getBoundingClientRect()
      const DROPDOWN_WIDTH = 224
      const GAP = 8
      const viewportLeft = rect.right + window.scrollX - DROPDOWN_WIDTH
      const minLeft = window.scrollX + 8
      const maxLeft = window.scrollX + window.innerWidth - DROPDOWN_WIDTH - 8
      const left = Math.max(minLeft, Math.min(viewportLeft, maxLeft))
      const top = rect.bottom + window.scrollY + GAP
      setDropdownCoords({ left, top })
    }
    window.addEventListener('resize', handle)
    return () => {
      window.removeEventListener('resize', handle)
    }
  }, [dropdownOpen])

  // debug log removed

  return (
    <header className="bg-white w-full border-b-2 h-14 xs:h-20 z-50">
      <div className="relative grid grid-cols-2 md:grid-cols-[auto,1fr,auto] max-w-custom-lg mx-0 md:mx-auto px-3 md:px-6 lg:px-8 w-full h-full items-center gap-6 justify-end-center m-auto">
        <div className="justify-self-start">
          <Logo />
        </div>

        {/* Middle column: search (default) or city categories (desktop) */}
        <div
          ref={centerRef}
          className="justify-self-center hidden md:flex md:justify-end md:min-w-0 w-full"
        >
          {!showLocationCategories && (!showSearchAsIcon || searchFocused) && (
            <Search
              searchFocused={searchFocused}
              setSearchFocused={setSearchFocused}
            />
          )}
          {showLocationCategories && renderCityCategoriesDesktop()}
        </div>

        {/* Right column */}
        <div className="justify-self-end flex items-center xs:gap-5 gap-4 xs:self-center relative">
          {!showLocationCategories &&
            showSearchAsIcon &&
            !searchFocused &&
            renderSearchAsIcon()}
          <Contact />
          <LanguageSwitcher
            onOpenChange={setLanguageMenuOpen}
            externalCloseKey={languageCloseSignal}
          />

          {showLocationCategories && isMobile && (
            <button
              onClick={() =>
                setMenuOpen((prev) => {
                  const next = !prev
                  if (next) {
                    // Close other menus when opening burger
                    setDropdownOpen(false)
                    setDropdownCoords(null)
                    setLanguageCloseSignal((v) => v + 1)
                  }
                  return next
                })
              }
              aria-label="Open categories menu"
              className="flex items-center"
            >
              <span
                style={{ width: '22px' }}
                className={`text-xl ${menuOpen ? 'far fa-circle-xmark' : 'fas fa-bars'}`}
              />
            </button>
          )}
          {showLocationCategories &&
            isMobile &&
            menuOpen &&
            renderCityCategoriesMobileMenu()}
        </div>
        {/* Mobile search mount for non-city pages */}
      </div>
      <CategoryDropdown
        isOpen={showLocationCategories && dropdownOpen}
        coords={dropdownCoords}
        items={matchedHeaderCategories.slice(
          isCategoryPage
            ? Math.max(numVisibleCategories - 1, 0)
            : numVisibleCategories,
        )}
        language={i18n.language}
        onClose={() => {
          setDropdownOpen(false)
          setDropdownCoords(null)
        }}
        getHref={getHref}
      />
      {!showLocationCategories && (
        <div className="md:hidden col-span-2">
          <Search
            searchFocused={searchFocused}
            setSearchFocused={setSearchFocused}
          />
        </div>
      )}
    </header>
  )
}
